import {
  HabitSchedule,
  ScheduleChangeMode,
  ScheduleEndMode,
  Timestamp,
} from '@griply/types';
import { subDays } from 'date-fns';
import isEqual from 'lodash/isEqual';

export enum ScheduleSubmitAction {
  Direct = 'direct',
  ConfirmChanges = 'confirm-changes',
  ConfirmEnd = 'confirm-end',
}

export type ScheduleSubmitResolution = { action: ScheduleSubmitAction };

export const resolveScheduleSubmit = (
  initial: HabitSchedule,
  updated: HabitSchedule,
): ScheduleSubmitResolution => {
  const hasStartDateChanged =
    initial.startDate.getTime() !== updated.startDate.getTime();
  const isStartDateInFuture =
    updated.startDate.getTime() > initial.startDate.getTime();
  const hasEntries = !!initial.entries?.length;

  if (hasStartDateChanged && isStartDateInFuture && hasEntries) {
    return { action: ScheduleSubmitAction.ConfirmEnd };
  }

  const initialConfig = { ...initial, startDate: null, endDate: null };
  const updatedConfig = { ...updated, startDate: null, endDate: null };
  const hasConfigChanges = !isEqual(initialConfig, updatedConfig);

  if (!hasStartDateChanged && !hasConfigChanges && hasEntries) {
    return { action: ScheduleSubmitAction.ConfirmChanges };
  }

  return { action: ScheduleSubmitAction.Direct };
};

export type ScheduleSplitResult = {
  schedule: HabitSchedule;
  splitOptions?: { originalScheduleEndDate: Date };
};

export const applyScheduleChangeMode = (
  schedule: HabitSchedule,
  mode: ScheduleChangeMode,
  today: Timestamp,
): ScheduleSplitResult => {
  if (mode === ScheduleChangeMode.Today) {
    const resetSchedule: HabitSchedule = {
      ...schedule,
      startDate: today,
      entries: [],
      completions: [],
      skips: [],
    };
    return {
      schedule: resetSchedule,
      splitOptions: { originalScheduleEndDate: subDays(today, 1) },
    };
  }

  return { schedule };
};

export type ScheduleEndSplitResult = {
  schedule: HabitSchedule;
  splitOptions: { originalScheduleEndDate: Date };
};

export const applyScheduleEndMode = (
  schedule: HabitSchedule,
  mode: ScheduleEndMode,
  today: Timestamp,
): ScheduleEndSplitResult => {
  const originalScheduleEndDate =
    mode === ScheduleEndMode.Today ? today : subDays(schedule.startDate, 1);

  const resetSchedule: HabitSchedule = {
    ...schedule,
    entries: [],
    completions: [],
    skips: [],
  };

  return {
    schedule: resetSchedule,
    splitOptions: { originalScheduleEndDate },
  };
};
